import { isDestroyed, isDestroying } from '@ember/destroyable';
import { concat } from '@ember/helper';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import { tracked } from '@glimmer/tracking';
import { PrismComboSelectInput } from '@mylighthouse/prism-ember';
import { task } from 'ember-concurrency';
import { t } from 'ember-intl';
import { timeout } from 'lumen/utils/waiters';

interface HotelAndMarketDropdownsHotelSelectionDropdownOldSignature {
  Args: {
    isDemoMode?: boolean;
  };
}

export default class HotelAndMarketDropdownsHotelSelectionDropdownOld extends Component<HotelAndMarketDropdownsHotelSelectionDropdownOldSignature> {
  @service declare access: Services['access'];
  @service declare ajax: Services['ajax'];
  @service declare destination: Services['destination'];
  // eslint-disable-next-line ember/no-unused-services
  @service declare featureToggle: Services['feature-toggle'];
  @service('hotel-and-market')
  declare hotelAndMarketService: Services['hotel-and-market'];
  @service declare router: Services['router'];

  @tracked isOpen = false;
  @tracked options = [];
  @tracked optionsFilter = '';
  @tracked page = 0;
  @tracked totalPages = 1;

  fetchOptionsTask = task({ restartable: true }, async () => {
    const data = {
      page: this.page,
      q: this.optionsFilter,
      market_id: this.hotelAndMarketService.market?.id,
    };

    // @ts-expect-error: Incorrect type
    let options = [];

    try {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const { meta, results } = await this.ajax.request(
        '/autocomplete/hotelsmarkets/',
        {
          data,
          type: 'GET',
        },
      );

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      if (results.length === 0) {
        return;
      }

      // Remove the hotel and market that the user is currently looking at
      const currentHotelId = this.hotelAndMarketService.hotel?.id;
      const currentMarketId = this.hotelAndMarketService.market?.id;
      const currentDestinationSubscriptionId =
        this.destination.destinationSubscription?.id;

      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
      const resultsFiltered = results.filter((result) => {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        const {
          hotel_id: hotelId,
          id: destinationSubscriptionId,
          market_id: marketId,
        } = result;

        if (hotelId !== null) {
          return `${hotelId}` !== currentHotelId;
        }

        if (marketId !== null) {
          return `${marketId}` !== currentMarketId;
        }

        if (destinationSubscriptionId !== null) {
          return (
            `${destinationSubscriptionId}` !== currentDestinationSubscriptionId
          );
        }

        return false;
      });

      const newOptions = this.hotelAndMarketService.formatAutocompleteResults(
        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument
        resultsFiltered,
        true,
      );

      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      options = [...options, ...newOptions];

      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      this.totalPages = Math.ceil(meta.results_count / meta.results_per_page);
    } catch {
      // Do nothing
    }

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
    function getIcon(type) {
      switch (type) {
        case 'destinationsubscription': {
          return 'location-pin';
        }

        case 'market': {
          return 'business-type-market';
        }

        default: {
          return 'business-type-hotel';
        }
      }
    }

    // @ts-expect-error: Incorrect type
    return options.map((option) => {
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      const {
        destination_subscription_id,
        hotel_id,
        market_id,
        name,
        products,
        subscription_type,
        type,
      } = option;

      return {
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        badge: subscription_type,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        destinationSubscriptionId: destination_subscription_id,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        hotelId: hotel_id,
        icon: getIcon(type),
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        label: name,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        marketId: market_id,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
        type,
        // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment, @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-member-access
        unsafe_subLabel: products.join(', '),
      };
    });
  });

  loadMoreTask = task({ restartable: true }, async () => {
    if (this.page + 1 < this.totalPages) {
      this.page += 1;
      const fetchedOptions = await this.fetchOptionsTask.perform();
      // @ts-expect-error: Incorrect type
      // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
      this.options = [...this.options, ...fetchedOptions];
    }
  });

  searchOptionTask = task({ restartable: true }, async (optionsFilter) => {
    await timeout();

    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    this.optionsFilter = optionsFilter;
    this.page = 0;
    // @ts-expect-error: Incorrect type
    this.options = await this.fetchOptionsTask.perform();
  });

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  get selectedValue() {
    if (this.hotelAndMarketService.hotel) {
      let selectedHotelName = '';

      if (this.args.isDemoMode) {
        selectedHotelName = this.hotelAndMarketService.hotel?.hotelinfo?.name;
      } else {
        selectedHotelName = this.hotelAndMarketService.hotel?.hotelinfo
          ?.brandCode
          ? `${this.hotelAndMarketService.hotel?.hotelinfo?.brandCode} - ${this.hotelAndMarketService.hotel?.hotelinfo?.name}`
          : this.hotelAndMarketService.hotel?.hotelinfo?.name;
      }

      return { label: selectedHotelName };
    }
    if (this.hotelAndMarketService.market) {
      // Market view
      const marketDisplayName = this.hotelAndMarketService.market?.displayName;

      // Append the user's email for staff users when looking at My Hotels
      if (
        this.hotelAndMarketService.isMyHotels &&
        this.access.isStaffUser &&
        this.hotelAndMarketService.market?.ownerEmail &&
        !this.args.isDemoMode
      ) {
        return {
          label: `${marketDisplayName} (${this.hotelAndMarketService.market?.ownerEmail})`,
        };
      }

      return { label: marketDisplayName };
    }
    if (this.destination.destinationSubscription) {
      return { label: this.destination.destinationSubscription.name };
    }

    return null;
  }

  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  parseQuery() {
    const qstr = window.location.search.substring(1);
    const query = {};

    // Only split if there is a query string
    if (qstr.length > 0) {
      qstr.split('&').forEach((item) => {
        const b = item.split('=');
        // @ts-expect-error: Incorrect type
        const queryKey = decodeURIComponent(b[0]);
        // @ts-expect-error: Incorrect type
        let queryValue = decodeURIComponent(b[1]);

        const re = /\[.*\]/;
        const reObj = /\{.*\}/;

        if (queryValue.match(reObj)) {
          // @ts-expect-error: Incorrect type
          query[queryKey] = '';
        } else if (queryValue.match(re)) {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          queryValue = queryValue.match(re)
            ? JSON.parse(queryValue)
            : queryValue;
          // @ts-expect-error: Incorrect type
          query[queryKey] = queryValue;
        } else {
          // @ts-expect-error: Incorrect type
          query[queryKey] = queryValue;
        }
      });
    }

    return query;
  }

  resetValues(): void {
    this.optionsFilter = '';
    this.page = 0;
    this.options = [];
  }

  @action
  // @ts-expect-error: Incorrect type
  // eslint-disable-next-line @typescript-eslint/explicit-function-return-type
  handleChange(option) {
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const { type } = option;
    // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
    const id =
      // eslint-disable-next-line @typescript-eslint/no-unsafe-member-access
      option.hotelId || option.marketId || option.destinationSubscriptionId;

    let targetRoute = this.router.currentRouteName;

    const queryParams = {};

    let parsedQueryParams = this.parseQuery();

    if (type === 'market') {
      const defaultProductRoute = this.access.hasSmartInsights
        ? 'market.smart-insights'
        : 'market.marketrates.index';

      // Added a . here because we want to check the market parent route
      // market-insight for example also has market in its route name
      // @ts-expect-error: Incorrect type
      if (targetRoute.indexOf('market.') === -1) {
        parsedQueryParams = {};
        targetRoute = defaultProductRoute;
      }

      // If there is a page parameter, always reset it to 1 in case of a market switch
      // @ts-expect-error: Incorrect type
      if (parsedQueryParams.page) {
        // @ts-expect-error: Incorrect type
        parsedQueryParams.page = '1';
      }

      // @ts-expect-error: Incorrect type
      if (targetRoute.includes('.spider-dashboard')) {
        const {
          // @ts-expect-error: Incorrect type
          currentRoute: { params, queryParams: dashboardQueryParams },
        } = this.router;

        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        return this.router.transitionTo(targetRoute, id, params.path, {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          queryParams: dashboardQueryParams,
        });
      }

      if (targetRoute === 'hotel.error' || targetRoute === 'market.error') {
        targetRoute = defaultProductRoute;
        // @ts-expect-error: Incorrect type
        queryParams.reason = null;
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.router.legacyTransitionTo(targetRoute, id, { queryParams });
      }
    } else if (type === 'hotel') {
      // @ts-expect-error: Incorrect type
      if (targetRoute.indexOf('hotel.') === -1) {
        parsedQueryParams = {};
        targetRoute = 'hotel.rates.index';
      }

      // @ts-expect-error: Incorrect type
      if (targetRoute.includes('.spider-dashboard')) {
        const {
          // @ts-expect-error: Incorrect type
          currentRoute: { params, queryParams: dashboardQueryParams },
        } = this.router;

        // eslint-disable-next-line @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-argument, @typescript-eslint/no-unsafe-member-access
        return this.router.transitionTo(targetRoute, id, params.path, {
          // eslint-disable-next-line @typescript-eslint/no-unsafe-assignment
          queryParams: dashboardQueryParams,
        });
      }

      /**
       * Budget & Forecast detail routes have a dynamic segment entry_id.
       * This should be reset when switching hotels.
       */
      if (
        targetRoute === 'hotel.settings.budget.detail' ||
        targetRoute === 'hotel.settings.forecast.detail'
      ) {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
        return this.router.legacyTransitionTo(
          targetRoute,
          id,
          { entry_id: undefined },
          {
            queryParams: parsedQueryParams,
          },
        );
      }

      /**
       * Budgdet & Forecast view mode should reset the entryId query param on hotel change.
       */
      if (targetRoute === 'hotel.budget' || targetRoute === 'hotel.forecast') {
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
        return this.router.legacyTransitionTo(targetRoute, id, {
          queryParams: { ...parsedQueryParams, entryId: undefined },
        });
      }

      if (targetRoute === 'hotel.error' || targetRoute === 'market.error') {
        targetRoute = 'hotel.rates';
        // @ts-expect-error: Incorrect type
        queryParams.reason = null;
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call
        this.router.legacyTransitionTo(targetRoute, id, { queryParams });
      }
    } else if (type === 'destinationsubscription') {
      // @ts-expect-error: Incorrect type
      if (targetRoute.indexOf('destination.') === -1) {
        parsedQueryParams = {};
        targetRoute = 'destination.index';
      }

      if (targetRoute === 'destination.reporting.dashboard') {
        // if switching destination sub while in dashboard, go to main index
        // @ts-expect-error: Incorrect type
        // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
        return this.router.legacyTransitionTo('destination.index', id);
      }
    }

    // @ts-expect-error: Incorrect type
    // eslint-disable-next-line @typescript-eslint/no-unsafe-call, @typescript-eslint/no-unsafe-return
    return this.router.legacyTransitionTo(targetRoute, id, {
      queryParams: parsedQueryParams,
    });
  }

  @action
  toggleDropdown(): void {
    if (isDestroyed(this) || isDestroying(this)) {
      return;
    }

    this.isOpen = !this.isOpen;

    if (this.isOpen) {
      void this.searchOptionTask.perform(this.optionsFilter);
    } else {
      this.resetValues();
    }
  }

  <template>
    <div
      class="hotel-selection-dropdown
        {{if this.isOpen 'hotel-selection-dropdown--full-width'}}"
    >
      <PrismComboSelectInput
        @disabled={{@isDemoMode}}
        @horizontalPosition="right"
        @isSearchable={{true}}
        @loadingMessage={{concat (t "general.loading") "..."}}
        @onChange={{this.handleChange}}
        @onClose={{this.toggleDropdown}}
        @onOpen={{this.toggleDropdown}}
        @onSearch={{this.searchOptionTask.perform}}
        @options={{this.options}}
        @unsafe_dropdownClass="hotel-selection-dropdown__content"
        @unsafe_isClampedToOneLine={{true}}
        @unsafe_onLoadMore={{this.loadMoreTask.perform}}
        @value={{this.selectedValue}}
        data-test-id="hotel-selection-dropdown-old"
      />
    </div>
  </template>
}
